import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "bg-[#FCFBFA] border-brand-line focus-visible:ring-brand-green focus-visible:border-brand-green";

export const Lbl = ({ children }) => (
  <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">{children}</Label>
);

export function Field({ label, value, onChange, placeholder, type = "text", testId, className = "" }) {
  return (
    <div className={className}>
      {label && <Lbl>{label}</Lbl>}
      <Input type={type} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} />
    </div>
  );
}

export function TextareaField({ label, value, onChange, placeholder, testId, rows = 3, disabled = false }) {
  return (
    <div className={disabled ? "opacity-50" : ""}>
      {label && <Lbl>{label}</Lbl>}
      <Textarea value={value ?? ""} placeholder={disabled ? "Not applicable" : placeholder} rows={rows} disabled={disabled}
        onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} />
    </div>
  );
}

export function SelectField({ label, value, onChange, options, placeholder = "Select…", testId }) {
  return (
    <div>
      {label && <Lbl>{label}</Lbl>}
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className={inputCls} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
              {typeof o === "string" ? o : o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function YesNo({ label, value, onChange, testId }) {
  const v = value === true ? "yes" : value === false ? "no" : "";
  return (
    <div>
      {label && <Lbl>{label}</Lbl>}
      <RadioGroup value={v} onValueChange={(val) => onChange(val === "yes")} className="flex gap-6 mt-1" data-testid={testId}>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-brand-ink">
          <RadioGroupItem value="yes" data-testid={testId ? `${testId}-yes` : undefined} /> Yes
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-brand-ink">
          <RadioGroupItem value="no" data-testid={testId ? `${testId}-no` : undefined} /> No
        </label>
      </RadioGroup>
    </div>
  );
}

export function CheckboxField({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer text-sm text-brand-ink py-1">
      <Checkbox checked={!!checked} onCheckedChange={(c) => onChange(!!c)} data-testid={testId} /> {label}
    </label>
  );
}

export function CheckGroup({ label, options, value = [], onChange, testId, exclusiveOption }) {
  const toggle = (opt) => {
    let set = new Set(value);
    if (exclusiveOption && opt === exclusiveOption) {
      onChange(set.has(opt) ? [] : [opt]);
      return;
    }
    set.delete(exclusiveOption);
    set.has(opt) ? set.delete(opt) : set.add(opt);
    onChange(Array.from(set));
  };
  return (
    <div>
      {label && <Lbl>{label}</Lbl>}
      <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1">
        {options.map((o) => (
          <label key={o} className="flex items-center gap-2 cursor-pointer text-sm text-brand-ink py-0.5">
            <Checkbox checked={value.includes(o)} onCheckedChange={() => toggle(o)}
              data-testid={testId ? `${testId}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : undefined} /> {o}
          </label>
        ))}
      </div>
    </div>
  );
}

export const SectionTitle = ({ children, hint }) => (
  <div className="mb-5">
    <h3 className="font-serif text-2xl text-brand-green">{children}</h3>
    {hint && <p className="text-sm text-brand-muted mt-1">{hint}</p>}
  </div>
);

export const SubHead = ({ children }) => (
  <div className="text-xs tracking-widest font-semibold text-brand-gold uppercase mt-6 mb-3 pb-1.5 border-b border-brand-line">{children}</div>
);
